import React, { useCallback, useContext, useEffect, useReducer, useState } from "react";
import {
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DeleteOutline, Edit, Search } from "@material-ui/icons";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import KnowledgeArticleModal from "../../components/KnowledgeArticleModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { canAccessKnowledgeBase } from "../../utils/userPermissions";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  searchField: {
    minWidth: 320,
    maxWidth: 520,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_RECORDS") {
    const records = action.payload || [];
    const newRecords = [];

    records.forEach(record => {
      const index = state.findIndex(item => item.id === record.id);
      if (index !== -1) {
        state[index] = record;
      } else {
        newRecords.push(record);
      }
    });

    return [...state, ...newRecords];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "DELETE_RECORD") {
    const id = action.payload;
    const index = state.findIndex(item => item.id === id);
    if (index !== -1) {
      state.splice(index, 1);
    }
    return [...state];
  }

  return state;
};

const KnowledgeBase = () => {
  const classes = useStyles();
  const [records, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [debouncedSearchParam, setDebouncedSearchParam] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasOpenAiPlan, setHasOpenAiPlan] = useState(null);
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;
  const history = useHistory();
  const { getPlanCompany } = usePlans();
  const hasKnowledgeBaseAccess = canAccessKnowledgeBase(user);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchParam(searchParam.trim());
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchParam]);

  useEffect(() => {
    async function fetchPlan() {
      if (!companyId) return;

      try {
        const planConfigs = await getPlanCompany(undefined, companyId);
        const planAllowsOpenAi = !!planConfigs?.plan?.useOpenAi;
        setHasOpenAiPlan(planAllowsOpenAi);

        if (!planAllowsOpenAi) {
          toast.error(
            "Esta empresa nao possui permissao para acessar essa pagina! Estamos lhe redirecionando."
          );
          setTimeout(() => {
            history.push(`/`);
          }, 1000);
        }
      } catch (err) {
        setHasOpenAiPlan(false);
        toastError(err);
      }
    }

    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadRecords = useCallback(async () => {
    if (!hasKnowledgeBaseAccess || hasOpenAiPlan !== true) return;

    setLoading(true);
    try {
      dispatch({ type: "RESET" });
      setTotalCount(0);

      let pageNumber = 1;
      let hasMore = true;

      while (hasMore) {
        const { data } = await api.get("/knowledge-articles", {
          params: {
            pageNumber,
            searchParam: debouncedSearchParam || undefined
          }
        });

        dispatch({ type: "LOAD_RECORDS", payload: data?.records || [] });
        setTotalCount(Number(data?.count || 0));

        hasMore = !!data?.hasMore;
        pageNumber += 1;
      }
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchParam, hasKnowledgeBaseAccess, hasOpenAiPlan]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (
      !socket ||
      !companyId ||
      !hasKnowledgeBaseAccess ||
      hasOpenAiPlan !== true
    ) {
      return undefined;
    }

    const eventName = `company-${companyId}-knowledge-article`;
    const handleEvent = () => loadRecords();

    socket.on(eventName, handleEvent);
    return () => socket.off(eventName, handleEvent);
  }, [socket, companyId, loadRecords, hasKnowledgeBaseAccess, hasOpenAiPlan]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRecord(null);
  };

  const handleOpenModal = (record = null) => {
    setSelectedRecord(record);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedRecord?.id) {
      return;
    }

    try {
      await api.delete(`/knowledge-articles/${selectedRecord.id}`);
      dispatch({ type: "DELETE_RECORD", payload: selectedRecord.id });
      toast.success("Artigo removido");
    } catch (error) {
      toastError(error);
    } finally {
      setConfirmOpen(false);
      setSelectedRecord(null);
    }
  };

  const formatScope = record => {
    const scopeParts = [];
    if (record?.prompt?.name) {
      scopeParts.push(`Prompt: ${record.prompt.name}`);
    }
    if (record?.queue?.name) {
      scopeParts.push(`Fila: ${record.queue.name}`);
    }
    return scopeParts.length ? scopeParts.join(" | ") : "Global";
  };

  const handleSearch = event => {
    setSearchParam(event.target.value);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={selectedRecord ? `Excluir artigo "${selectedRecord.title}"?` : "Excluir artigo?"}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        Essa acao nao pode ser revertida.
      </ConfirmationModal>

      <KnowledgeArticleModal
        open={modalOpen}
        articleId={selectedRecord?.id}
        onClose={handleCloseModal}
        onSaved={loadRecords}
      />

      {!hasKnowledgeBaseAccess || hasOpenAiPlan === false ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>Base de Conhecimento ({totalCount || records.length})</Title>
            <MainHeaderButtonsWrapper>
              <TextField
                className={classes.searchField}
                placeholder="Pesquisar por titulo, tags, colecao, escopo, status ou conteudo"
                type="search"
                value={searchParam}
                onChange={handleSearch}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search style={{ color: "gray" }} />
                    </InputAdornment>
                  )
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleOpenModal()}
                style={{ borderRadius: "22px" }}
              >
                Novo artigo
              </Button>
            </MainHeaderButtonsWrapper>
          </MainHeader>

          <Paper className={classes.mainPaper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Titulo</TableCell>
                  <TableCell align="left">Colecao</TableCell>
                  <TableCell align="left">Escopo</TableCell>
                  <TableCell align="left">Status</TableCell>
                  <TableCell align="center">Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map(record => (
                  <TableRow key={record.id}>
                    <TableCell align="left">{record.title}</TableCell>
                    <TableCell align="left">{record.collection || "-"}</TableCell>
                    <TableCell align="left">{formatScope(record)}</TableCell>
                    <TableCell align="left">{record.isActive ? "Ativo" : "Inativo"}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenModal(record)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedRecord(record);
                          setConfirmOpen(true);
                        }}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default KnowledgeBase;
